/**
 * FCM 메시지 payload에서 알림 표시에 필요한 데이터를 추출한다.
 *
 * 타입별 extractor를 맵으로 관리하여 새 타입 추가 시 한 곳만 수정하면 된다.
 * 동일한 로직이 public/fcm-message-parser.js에도 있으며,
 * Service Worker에서는 importScripts로 해당 파일을 사용한다.
 */

interface NotificationData {
  title?: string;
  body?: string;
  icon?: string;
  messageThreadId?: string;
  deeplink?: string;
  buttonText?: string;
}

interface NotificationFallback {
  title?: string;
  body?: string;
  icon?: string;
}

function parsePayload<T>(data?: Record<string, string>, expectedType?: string): T | null {
  if (expectedType && data?.type !== expectedType) return null;
  if (!data?.payload) return null;
  try {
    return JSON.parse(data.payload) as T;
  } catch {
    return null;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Extractor = (parsed: any, fallback?: NotificationFallback) => NotificationData;

const extractors: Record<string, Extractor> = {
  FcmGeneralMessage: (parsed, fallback) => ({
    title: parsed?.title || fallback?.title,
    body: parsed?.message || fallback?.body,
    icon: parsed?.iconImageUrl || fallback?.icon,
    deeplink: parsed?.deeplink,
    buttonText: parsed?.buttonText,
  }),
  GcmTextMessage: (parsed) => ({
    title: parsed?.friendInfo?.simpleName,
    body: parsed?.textMessageInfo?.messageContent,
    icon: parsed?.friendInfo?.smallProfileImageUrl,
    messageThreadId: parsed?.messageThreadId,
  }),
};

export function extractNotification(
  data?: Record<string, string>,
  notificationFallback?: NotificationFallback
): NotificationData {
  const type = data?.type;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parsed = parsePayload<Record<string, any>>(data, type);
  const extractor = type ? extractors[type] : undefined;

  if (extractor) {
    return extractor(parsed, notificationFallback);
  }

  return {
    title: notificationFallback?.title,
    body: notificationFallback?.body,
    icon: notificationFallback?.icon,
  };
}

export { parsePayload };
export type { NotificationData };
